import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { ySyncPlugin, yCursorPlugin, yUndoPlugin } from 'y-prosemirror';
import { getContrastColor } from '../utils/color';
import {
  Plus, Search, X, Building2, Phone, Mail, MapPin, FileText,
  Upload, Pencil, Trash2, ChevronRight, Users, ExternalLink, ArrowLeft,
} from 'lucide-react';
import api from '../utils/api';
import EditorToolbar from '../components/EditorToolbar';
import PageHeader from '../components/PageHeader';

const COLORS = ['#f87171','#fb923c','#facc15','#4ade80','#22d3ee','#818cf8','#e879f9','#f472b6'];
function getUserColor(id) { return COLORS[(id || 0) % COLORS.length]; }
function cursorBuilder(user) {
  const el = document.createElement('span');
  el.style.cssText = `border-left:2px solid ${user.color};margin-left:-1px;margin-right:-1px;position:relative;`;
  const label = document.createElement('div');
  label.style.cssText = `position:absolute;top:-1.4em;left:-1px;font-size:10px;padding:0 4px;border-radius:3px;background:${user.color};color:${getContrastColor(user.color)};white-space:nowrap;pointer-events:none;`;
  label.textContent = user.name;
  el.appendChild(label);
  return el;
}

function PartnerEditor({ partnerId }) {
  const user = JSON.parse(localStorage.getItem('mfg_user') || '{}');
  const [wsStatus, setWsStatus] = useState('connecting');

  const { ydoc, provider } = useMemo(() => {
    const doc = new Y.Doc();
    const token = localStorage.getItem('mfg_token');
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const isLocal = ['localhost', '127.0.0.1', '100.116.73.97'].includes(window.location.hostname);
    const wsHost = isLocal ? `${window.location.hostname}:3800` : window.location.host;
    const prov = new WebsocketProvider(`${wsProtocol}//${wsHost}/yjs`, `partner-${partnerId}`, doc, { params: { token } });
    return { ydoc: doc, provider: prov };
  }, [partnerId]);

  const YjsExt = useMemo(() => {
    const fragment = ydoc.getXmlFragment('content');
    return Extension.create({
      name: 'yjs',
      addProseMirrorPlugins() {
        return [
          ySyncPlugin(fragment),
          yCursorPlugin(provider.awareness, { cursorBuilder }),
          yUndoPlugin(),
        ];
      },
    });
  }, [ydoc, provider]);

  const editor = useEditor({
    extensions: [StarterKit.configure({ history: false }), TextStyle, Color, Highlight.configure({ multicolor: true }), Placeholder.configure({ placeholder: '거래처 메모, 계약 내용, 특이사항 등을 자유롭게 기록하세요...' }), YjsExt],
    editorProps: {
      attributes: { class: 'prose prose-invert max-w-none min-h-[200px] focus:outline-none px-4 py-3 text-sm text-gray-200' },
    },
  }, [YjsExt]);

  useEffect(() => {
    provider.awareness.setLocalStateField('user', { name: user.name || '사용자', color: getUserColor(user.id) });
    provider.on('status', ({ status }) => setWsStatus(status));
    if (provider.wsconnected) setWsStatus('connected');
    return () => { provider.destroy(); ydoc.destroy(); };
  }, [provider, ydoc]);

  return (
    <div className="border border-gray-700 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700 bg-gray-800/50">
        <span className="text-xs font-medium text-gray-400">협업 메모</span>
        <span className={`text-[10px] px-2 py-0.5 rounded-full ${wsStatus === 'connected' ? 'bg-green-900/50 text-green-400' : 'bg-gray-700 text-gray-500'}`}>
          {wsStatus === 'connected' ? '실시간 동기화' : '연결 중...'}
        </span>
      </div>
      {editor && <EditorToolbar editor={editor} />}
      <EditorContent editor={editor} />
    </div>
  );
}

function PartnerModal({ partner, onClose, onSave }) {
  const [form, setForm] = useState({
    name: partner?.name || '',
    contactName: partner?.contactName || '',
    contactPhone: partner?.contactPhone || '',
    email: partner?.email || '',
    address: partner?.address || '',
    bizType: partner?.bizType || '',
    bizItem: partner?.bizItem || '',
    bizRegNo: partner?.bizRegNo || '',
    memo: partner?.memo || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-800">
          <h2 className="text-sm font-semibold text-white">{partner ? '거래처 수정' : '거래처 추가'}</h2>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-3">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">거래처명 *</label>
            <input className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand"
              placeholder="회사명" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} autoFocus required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">대표자</label>
              <input className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand"
                placeholder="홍길동" value={form.contactName} onChange={e => setForm(p => ({ ...p, contactName: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">전화번호</label>
              <input className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand"
                placeholder="010-0000-0000" value={form.contactPhone} onChange={e => setForm(p => ({ ...p, contactPhone: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">업태</label>
              <input className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand"
                placeholder="제조" value={form.bizType} onChange={e => setForm(p => ({ ...p, bizType: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">종목</label>
              <input className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand"
                placeholder="전자제품" value={form.bizItem} onChange={e => setForm(p => ({ ...p, bizItem: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">이메일</label>
              <input type="email" className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand"
                placeholder="contact@company.com" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">사업자등록번호</label>
              <input className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand"
                placeholder="000-00-00000" value={form.bizRegNo} onChange={e => setForm(p => ({ ...p, bizRegNo: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">주소</label>
            <input className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand"
              placeholder="서울시 강남구..." value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} />
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs text-gray-400 border border-gray-700 rounded-xl hover:bg-gray-800">취소</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-xs text-white bg-brand rounded-xl hover:bg-brand/80 disabled:opacity-50">
              {saving ? '저장 중...' : partner ? '수정' : '추가'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FileUploadRow({ label, url, partnerId, field, onUploaded }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('field', field);
    const res = await api.post(`/partners/${partnerId}/upload`, fd);
    onUploaded(field, res.data.url, res.data.originalName);
    setUploading(false);
    e.target.value = '';
  };

  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-800">
      <span className="text-xs text-gray-400 w-28 flex-shrink-0">{label}</span>
      {url ? (
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <a href={url} target="_blank" rel="noreferrer"
            className="flex items-center gap-1 text-xs text-brand hover:underline truncate">
            <FileText size={12} /> 파일 보기 <ExternalLink size={10} />
          </a>
          <button onClick={() => inputRef.current?.click()} className="text-xs text-gray-500 hover:text-gray-300 flex-shrink-0">
            교체
          </button>
        </div>
      ) : (
        <button onClick={() => inputRef.current?.click()}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-brand">
          <Upload size={12} /> {uploading ? '업로드 중...' : '파일 업로드'}
        </button>
      )}
      <input ref={inputRef} type="file" className="hidden" onChange={handleUpload} />
    </div>
  );
}

export default function PartnersPage() {
  const [partners, setPartners] = useState([]);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const res = await api.get('/partners');
    setPartners(res.data);
    if (res.data.length > 0) setSelected(res.data[0]);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = partners.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.contactName || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = async form => {
    if (modal?.partner) {
      const res = await api.put(`/partners/${modal.partner.id}`, form);
      setPartners(prev => prev.map(p => p.id === modal.partner.id ? res.data : p));
      if (selected?.id === modal.partner.id) setSelected(res.data);
    } else {
      const res = await api.post('/partners', form);
      setPartners(prev => [...prev, res.data]);
      setSelected(res.data);
    }
    setModal(null);
  };

  const handleDelete = async (p) => {
    if (!confirm(`'${p.name}'을(를) 삭제하시겠습니까?`)) return;
    await api.delete(`/partners/${p.id}`);
    setPartners(prev => prev.filter(x => x.id !== p.id));
    if (selected?.id === p.id) setSelected(null);
  };

  const handleFileUploaded = (field, url) => {
    setSelected(prev => ({ ...prev, [field]: url }));
    setPartners(prev => prev.map(p => p.id === selected.id ? { ...p, [field]: url } : p));
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader icon={Building2} title="거래처 관리" count={filtered.length}>
        <button onClick={() => setModal({ partner: null })}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80">
          <Plus size={14} /> 거래처 추가
        </button>
      </PageHeader>

      <div className="flex gap-4 flex-1 min-h-0 overflow-hidden">
      {/* 좌측 목록 */}
      <div className={`sm:w-64 sm:flex-shrink-0 flex-col gap-3 ${selected ? 'hidden sm:flex' : 'flex w-full'}`}>
        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center bg-gray-900 border border-gray-800 rounded-xl px-2.5">
            <Search size={13} className="text-gray-500 flex-shrink-0" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="거래처 검색..."
              className="flex-1 bg-transparent text-white text-xs py-2 px-2 focus:outline-none" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto flex flex-col gap-1">
          {filtered.length === 0 && (
            <div className="text-center py-10 text-xs text-gray-600">
              {search ? '검색 결과 없음' : '거래처를 추가하세요'}
            </div>
          )}
          {filtered.map(p => (
            <button key={p.id} onClick={() => setSelected(p)}
              className={`w-full text-left px-3 py-2.5 rounded-xl transition-all ${
                selected?.id === p.id ? 'bg-brand/20 border border-brand/30' : 'hover:bg-gray-800 border border-transparent'
              }`}>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-gray-700 flex items-center justify-center flex-shrink-0">
                  <Building2 size={13} className="text-gray-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white truncate">{p.name}</p>
                  {p.contactName && <p className="text-xs text-gray-500 truncate">{p.contactName}</p>}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* 우측 상세 */}
      <div className={`flex-1 min-w-0 overflow-y-auto ${!selected ? 'hidden sm:flex sm:items-center sm:justify-center' : 'block'}`}>
        {!selected ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-600">
            <Building2 size={40} className="mb-3 opacity-30" />
            <p className="text-sm">거래처를 선택하거나 추가하세요</p>
          </div>
        ) : (
          <div className="flex flex-col gap-5 pb-6">
            {/* 모바일 뒤로가기 */}
            <button className="sm:hidden flex items-center gap-1.5 text-xs text-gray-400 hover:text-white -mb-2"
              onClick={() => setSelected(null)}>
              <ArrowLeft size={14} /> 목록으로
            </button>
            {/* 상단 헤더 */}
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">{selected.name}</h2>
                {selected.creator && (
                  <p className="text-xs text-gray-500 mt-0.5">등록: {selected.creator.name}</p>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setModal({ partner: selected })}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-400 border border-gray-700 rounded-xl hover:bg-gray-800 hover:text-white">
                  <Pencil size={12} /> 수정
                </button>
                <button onClick={() => handleDelete(selected)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-400 border border-red-400/30 rounded-xl hover:bg-red-400/10">
                  <Trash2 size={12} /> 삭제
                </button>
              </div>
            </div>

            {/* 기본 정보 카드 */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">기본 정보</h3>
              <div className="grid grid-cols-2 gap-4">
                <InfoRow icon={Users} label="대표자" value={selected.contactName} />
                <InfoRow icon={Phone} label="전화번호" value={selected.contactPhone}
                  href={selected.contactPhone ? `tel:${selected.contactPhone}` : null} />
                <InfoRow icon={FileText} label="사업자번호" value={selected.bizRegNo} />
                <InfoRow icon={Mail} label="이메일" value={selected.email}
                  href={selected.email ? `mailto:${selected.email}` : null} />
                <InfoRow icon={FileText} label="업태" value={selected.bizType} />
                <InfoRow icon={FileText} label="종목" value={selected.bizItem} />
                {selected.address && (
                  <div className="col-span-2">
                    <InfoRow icon={MapPin} label="주소" value={selected.address} />
                  </div>
                )}
              </div>
            </div>

            {/* 파일 */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">첨부 파일</h3>
              <FileUploadRow
                label="사업자등록증" url={selected.bizRegFileUrl}
                partnerId={selected.id} field="bizRegFileUrl"
                onUploaded={handleFileUploaded}
              />
              <FileUploadRow
                label="회사소개서" url={selected.introFileUrl}
                partnerId={selected.id} field="introFileUrl"
                onUploaded={handleFileUploaded}
              />
            </div>

            {/* 협업 에디터 */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">공유 메모</h3>
              <PartnerEditor key={selected.id} partnerId={selected.id} />
            </div>
          </div>
        )}
      </div>
      </div>

      {modal && (
        <PartnerModal partner={modal.partner} onClose={() => setModal(null)} onSave={handleSave} />
      )}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, href }) {
  if (!value) return (
    <div className="flex items-center gap-2">
      <Icon size={13} className="text-gray-600 flex-shrink-0" />
      <div>
        <p className="text-[10px] text-gray-600">{label}</p>
        <p className="text-xs text-gray-600 italic">미입력</p>
      </div>
    </div>
  );
  return (
    <div className="flex items-center gap-2">
      <Icon size={13} className="text-gray-400 flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] text-gray-500">{label}</p>
        {href ? (
          <a href={href} className="text-xs text-brand hover:underline truncate block">{value}</a>
        ) : (
          <p className="text-xs text-white truncate">{value}</p>
        )}
      </div>
    </div>
  );
}
